import React, {useState, useEffect} from "react";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { MediaScore } from "@/types";
import { interpolateDivergant, toIndex, toPercentage } from "@/utils";

interface ScoreCardProps {
  name: string;
  data: MediaScore[];
  score: string;
}

interface Params {
  minScore: number;
  maxScore: number;
  header: (string | number)[];
}
const defaultParams = {minScore: 0, maxScore: 1, header: []}

const MediaScoreCard: React.FC<ScoreCardProps> = ({
  name,
  data,
  score,
}) => {
  const theme = useTheme();

  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const [params, setParams] = useState<Params>(defaultParams);

  useEffect(() => {

    const maxScore = Math.max.apply(
      Math,
      data.map((x) => Number(x[score as keyof MediaScore])),
    );

    const minScore =
      score === "rate"
        ? Math.min.apply(
          Math,
          data.map((x) => Number(x[score as keyof MediaScore])),
        )
        : Math.max(1 - (maxScore - 1), 0);

    const header = getHeaderValues(
      data.map(x => Number(x[score as keyof MediaScore])),
      score === "rate" ? 0.1 : 0.1
    )

    setParams({minScore, maxScore, header})

  }, [data, score])

  const calcPercentage = (value: number) => {
    const mx = params.maxScore ? params.maxScore : 1;
    const mn = params.minScore ? params.minScore : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  const getHeaderValues = (values: number[], step: number) => {

    if (score === 'rate') {
      let start = (Math.round((Math.min(...values) * 100) / 10) * 10) / 100;
      let stop = (Math.ceil((Math.max(...values) * 100) / 5) * 5) / 100;
      let numSteps = Math.ceil((stop - start) / step);
      if (numSteps > 6) {
        step = step * 2
      } else if (numSteps < 4) {
        step = step / 2
      }

      return Array.from(
        { length: (stop - start) / step + 1 },
        (v, s) => start + s * step
      ).map(x => formatFunc(x, 0));

    } else {
      return ['None', '', 'Low', '', 'Medium', '', 'High', '']
    }
  }

  return (
    <Card
      sx={{
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        display: 'flex',
        flexDirection: 'column',
        height: '560px',
        overflow: 'hidden'
      }}
    >
      <CardHeader title={name} slotProps={{ title: { variant: "body1" } }} />
      <CardContent
        sx={{
          pt: 0,
          pb: 2,
          "&:last-child": { pb: 2 },
          height: '600px',
          overflowY: "auto"
        }}
      >
        <Grid container spacing={1}>

          {/* Header */}
          <Grid size={5}>

          </Grid>
          <Grid
            size={7}
            display="flex"
            justifyContent="space-between"
            sx={{
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
            }}
          >
            {params.header.map((value, idx) => (
              <Typography key={idx} variant="caption">{value}</Typography>
            ))}
          </Grid>

          {/* Scores */}
          {data.map((variable, idx) => {

            const value = Number(variable[score as keyof MediaScore]);

            return (
              <React.Fragment key={idx}>
                <Grid size={5}>
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {variable.tag_name}
                  </Typography>
                </Grid>
                <Grid size={7}>
                  <HelpBox title={formatFunc(value)}>
                    <Box
                      height={24}
                      borderRadius="0px 4px 4px 0px"
                      width={`${calcPercentage(value) * 100}%`}
                      sx={{
                        backgroundColor: interpolateDivergant(
                          theme.palette.secondary.main,
                          theme.palette.info.main,
                          theme.palette.error.main,
                          1 - calcPercentage(value),
                        ),
                      }}
                    />
                  </HelpBox>
                </Grid>
              </React.Fragment>
            )}
          )}
        </Grid>

      </CardContent>
    </Card>
  );
};

export default MediaScoreCard;
